import { useEffect, useState, useCallback } from 'react'
import { useAppContext } from '@/components/AppContext'
import { SubscriptionOrder } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import styled from 'styled-components'
import {
    Card,
    Col,
    Input,
    Row,
    Select,
    Space,
    message,
    Table,
    Tag,
    Button,
    Tooltip,
    Spin,
    Modal
} from 'antd'

import { KaggUserPropertyPayload } from '@a4b/api/src/modules/Kagg/type'
import { openErrorNotification } from '@/utils'
import { CustomLoader } from '@/components/Loader'

const { Search } = Input
const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  margin-top: 60px;

  .ant-card-body {
    flex-grow: 1;
  }
  .ant-card-head-wrapper {
    display: block;
    width: 100%;
  }
  .ant-card-head-title {
    flex-basis: 30%;
    padding-bottom: 0px;
  }
  .ant-card-extra {
    flex-basis: 70%;
  }
`

interface FiltersType {
    orderStatus: string | undefined
    bookingId: string
    contactNumber: string
    userId: string
    bookingWhatsapp: string
}

const SubscriptionOrders = () => {
    const { networkInstance, monetisationRoles, userProfile, app } = useAppContext()
    const { monetisationOfferingsApi, kagg } = networkInstance.clientWithHeaders
    const [orderData, setOrderData] = useState<SubscriptionOrder[]>()
    const [messageApi, contextHolder] = message.useMessage()
    const [loading, setLoading] = useState<boolean | null>(null)
    const [userId, setUserId] = useState<string>('');
    const [isUserPropLoading, setIsUserPropLoading] = useState(false)
    const [cancellingBookingId, setCancellingBookingId] = useState<number | null>(null)

    // Get currency symbol based on country code
    const getCurrencySymbol = (countryCode?: string) => {
        return countryCode === 'US' ? '$' : '₹'
    }
    const [filters, setFilters] = useState<FiltersType>({
        orderStatus: undefined,
        bookingId: '',
        contactNumber: '',
        userId: '',
        bookingWhatsapp: '',
    })

    const getOrders = useCallback(async () => {
        setLoading(true)
        try {
            const orderResponse = await monetisationOfferingsApi.orderApi.getSubscriptionOrders(
                filters.orderStatus || '',
                filters.bookingId,
                filters.contactNumber,
                filters.userId,
                filters.bookingWhatsapp,
            )
            setOrderData(orderResponse?.data?.data?.orders || [])
            setLoading(false)
        } catch (error) {
            setLoading(false)
            console.log("Error: ", error)
        }
    }, [filters.orderStatus, filters.bookingId, filters.contactNumber, filters.userId, monetisationOfferingsApi.orderApi])

    useEffect(() => {
        if (filters.bookingId || filters.userId || filters.contactNumber || filters.orderStatus) {
            getOrders()
        }
    }, [getOrders])

    const filterOrders = (type: string, value: string) => {
        setFilters((prevFilters: FiltersType) => ({
            ...prevFilters,
            [type]: value
        }))
    }

    const fetchUserIdByContact = async (contactNumber: string) => {
        try {
            setIsUserPropLoading(true)
            const payload: KaggUserPropertyPayload = {
                contact_numbers: [contactNumber]
            }
            const response = await kagg.getUserData(payload)
            return response?.data?.data?.users?.[0]?.user_id
        } catch (error) {
            openErrorNotification(error)
            return
        } finally {
            setIsUserPropLoading(false)
        }
    }

    const validateContactNumber = (value: string): string | null => {
        if (value) {
            if (value.length !== 10 || isNaN(Number(value))) {
                message.error('Please enter a valid 10-digit contact number.')
                return null
            }
            return `+91${value}`
        }
        return null
    }

    const handleContactSearch = async (value: string) => {
        if (!value) {
            setFilters(prev => ({
                ...prev,
                userId: '',
            }))
            setUserId('')
            return
        }

        const formattedValue = validateContactNumber(value)
        if (!formattedValue) { return }

        const userId = await fetchUserIdByContact(formattedValue)
        if (!userId) {
            message.warning('Contact number not found.');
            return
        }

        setFilters(prev => ({
            ...prev,
            userId: userId,
        }))
        setUserId(userId)
    }

    const handleCancelSubscription = async (bookingId: number, subscriptionData: SubscriptionOrder) => {
        let remarksValue = '';
        let modalInstance: any;

        const updateModal = () => {
            modalInstance.update({
                okButtonProps: {
                    danger: true,
                    size: 'large',
                    disabled: !remarksValue.trim()
                }
            });
        };

        modalInstance = Modal.confirm({
            title: null,
            icon: null,
            width: 600,
            centered: true,
            content: (
                <div>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_6877_25503)">
                                <path d="M20 0C8.95536 0 0 8.95536 0 20C0 31.0446 8.95536 40 20 40C31.0446 40 40 31.0446 40 20C40 8.95536 31.0446 0 20 0ZM20 36.6071C10.8304 36.6071 3.39286 29.1696 3.39286 20C3.39286 10.8304 10.8304 3.39286 20 3.39286C29.1696 3.39286 36.6071 10.8304 36.6071 20C36.6071 29.1696 29.1696 36.6071 20 36.6071Z" fill="#1890FF" />
                                <path d="M17.8564 27.8571C17.8564 28.4255 18.0822 28.9705 18.4841 29.3724C18.8859 29.7742 19.431 30 19.9993 30C20.5676 30 21.1127 29.7742 21.5145 29.3724C21.9164 28.9705 22.1422 28.4255 22.1422 27.8571C22.1422 27.2888 21.9164 26.7438 21.5145 26.3419C21.1127 25.9401 20.5676 25.7143 19.9993 25.7143C19.431 25.7143 18.8859 25.9401 18.4841 26.3419C18.0822 26.7438 17.8564 27.2888 17.8564 27.8571V27.8571ZM18.9279 22.8571H21.0707C21.2672 22.8571 21.4279 22.6964 21.4279 22.5V10.3571C21.4279 10.1607 21.2672 10 21.0707 10H18.9279C18.7314 10 18.5707 10.1607 18.5707 10.3571V22.5C18.5707 22.6964 18.7314 22.8571 18.9279 22.8571Z" fill="#1890FF" />
                            </g>
                            <defs>
                                <clipPath id="clip0_6877_25503">
                                    <rect width="40" height="40" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, marginTop: '16px' }}>
                            Are You Sure?
                        </h3>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 16px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 8,
                        marginBottom: 24
                    }}>
                        <div>
                            <div style={{ fontWeight: 500 }}>Booking ID - {bookingId}</div>
                            <div style={{ fontWeight: 500, marginTop: '4px' }}>
                                Subscription Amount - {getCurrencySymbol(subscriptionData.country_code)}{subscriptionData.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>

                    <p style={{ marginBottom: '24px', color: '#595959', fontSize: '14px' }}>
                        Are you sure you want to cancel this subscription? This action cannot be undone.
                    </p>

                    <div style={{ marginBottom: 24 }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#262626'
                        }}>
                            <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
                            Remarks
                        </label>
                        <Input.TextArea
                            placeholder="Please provide a reason for cancellation..."
                            rows={2}
                            onChange={(e) => {
                                remarksValue = e.target.value;
                                updateModal();
                            }}
                            style={{ borderRadius: 8 }}
                        />
                        {/* {!remarksValue.trim() && (
                            <div style={{
                                color: '#ff4d4f',
                                fontSize: '12px',
                                marginTop: '4px'
                            }}>
                                Remarks are required to cancel subscription
                            </div>
                        )} */}
                    </div>
                </div>
            ),
            okText: 'Yes, Cancel Subscription',
            cancelText: 'Cancel',
            okButtonProps: {
                danger: true,
                size: 'large',
                disabled: true // Initially disabled until remarks are provided
            },
            cancelButtonProps: {
                size: 'large'
            },
            onOk: async () => {
                if (!remarksValue.trim()) {
                    message.error('Please provide remarks for cancellation.');
                    return Promise.reject();
                }

                try {
                    setCancellingBookingId(bookingId)
                    await monetisationOfferingsApi.orderApi.cancelSubscription(bookingId, remarksValue)
                    message.success('Subscription cancelled successfully!')

                    await getOrders()
                } catch (error) {
                    console.error('Error cancelling subscription:', error)
                    message.error('Failed to cancel subscription. Please try again.')
                } finally {
                    setCancellingBookingId(null)
                }
            }
        })
    }

    const columns = [
        {
            title: 'Order Id',
            dataIndex: 'id',
            key: 'id',
            width: 200,
            render: (id: string) => (
                <Tooltip placement="topLeft" title={id}>
                    <div style={{
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        lineHeight: '1.2'
                    }}>
                        {id}
                    </div>
                </Tooltip>),
        },
        {
            title: 'Booking Id',
            dataIndex: 'booking_id',
            key: 'booking_id',
        },
        {
            title: 'Store Name',
            dataIndex: 'store_name',
            key: 'store_name',
        },
        {
            title: 'User Id',
            dataIndex: 'user_id',
            key: 'user_id',
            ellipsis: {
                showTitle: false,
            },
            render: (user_id: string) => (
                <Tooltip placement="topLeft" title={user_id}>
                    {user_id}
                </Tooltip>),
        },
        {
            title: 'Store Id',
            dataIndex: 'store_id',
            key: 'store_id',
            width: 200,
            render: (store_id: string) => (
                <Tooltip placement="topLeft" title={store_id}>
                    <div style={{
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        lineHeight: '1.2'
                    }}>
                        {store_id}
                    </div>
                </Tooltip>),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (text: string) => {
                if (text) {
                    let color = 'green'
                    if (text === 'active') {
                        color = '#52c41a'
                    } else if (text === 'cancelled') {
                        color = '#ff4d4f'
                    }
                    return (
                        <Tag color={color} key={text} style={{ width: 'fit-content' }}>
                            {text.toUpperCase()}
                        </Tag>
                    )
                }
                return "-"
            }
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number, record: SubscriptionOrder) => {
                return `${getCurrencySymbol(record.country_code)}${amount.toFixed(2)}`
            }
        },
        {
            title: 'SKU Details',
            dataIndex: 'sku',
            key: 'sku',
            render: (skus: any[], record: SubscriptionOrder) => {
                if (skus && skus.length > 0) {
                    return skus.map((sku, index) => (
                        <div key={sku.id}>
                            {sku.name} - {getCurrencySymbol(record.country_code)}{sku.amount.toFixed(2)}
                            {index < skus.length - 1 && <br />}
                        </div>
                    ))
                }
                return '-'
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 180,
            render: (text: string, record: SubscriptionOrder) => {
                const isCurrentlyCancelling = cancellingBookingId === record.booking_id

                if (record?.status === 'initiated' || record?.status === 'completed' || record?.status === 'cancelled') {
                    return (
                        <Button
                            type="primary"
                            disabled
                            style={{ backgroundColor: '#f5f5f5', borderColor: '#d9d9d9', color: '#bfbfbf' }}
                        >
                            Cancel Subscription
                        </Button>
                    )
                } else {
                    return (
                        <Button
                            type="primary"
                            danger
                            loading={isCurrentlyCancelling}
                            style={{ backgroundColor: '#FF4D4F', borderColor: '#FF4D4F' }}
                            onClick={() => handleCancelSubscription(record.booking_id, record)}
                            disabled={isCurrentlyCancelling}
                        >
                            {isCurrentlyCancelling ? 'Cancelling...' : 'Cancel Subscription'}
                        </Button>
                    )
                }
            }
        }
    ]

    return (
        <Container
            title='Manage Subscriptions'
            style={{ width: '100%', display: 'flex', flexDirection: 'column', marginTop: '0px' }}
            extra={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Row style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                        <Search
                            placeholder='Booking Id'
                            style={{ width: '12%', marginRight: '10px' }}
                            onSearch={(value: string) => {
                                filterOrders('bookingId', value)
                            }}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    filterOrders('bookingId', '')
                                }
                            }}
                            allowClear
                        />
                        <Search
                            placeholder='User Id'
                            style={{ width: '12%', marginRight: '10px' }}
                            value={userId}
                            onChange={(e) => {
                                setUserId(e?.target?.value)
                                if (!e.target.value) {
                                    filterOrders('userId', '')
                                }
                            }}
                            onSearch={(value: string) => {
                                filterOrders('userId', value)
                            }}
                            allowClear
                        />
                        <Search
                            placeholder='Contact Number'
                            style={{ width: '20%', marginRight: '10px' }}
                            addonBefore='+91'
                            maxLength={10}
                            minLength={10}
                            inputMode='numeric'
                            onSearch={handleContactSearch}
                            allowClear
                            loading={isUserPropLoading}
                        />
                        {/* <Search
                            placeholder='WhatsApp Number'
                            style={{ width: '15%', marginRight: '10px' }}
                            addonBefore='+91'
                            maxLength={10}
                            minLength={10}
                            inputMode='numeric'
                            onSearch={(value: string) => {
                                if (value) {
                                    const formattedValue = validateContactNumber(value)
                                    if (formattedValue) {
                                        filterOrders('bookingWhatsapp', formattedValue)
                                    }
                                } else {
                                    filterOrders('bookingWhatsapp', '')
                                }
                            }}
                            allowClear
                        /> */}

                        <Select
                            size='middle'
                            placeholder="Order Status"
                            style={{ width: '11%', marginRight: '10px' }}
                            value={filters?.orderStatus}
                            options={[
                                { label: 'Active', value: 'active' },
                                { label: 'Cancelled', value: 'cancelled' }
                            ]}
                            onChange={(value: string) => {
                                filterOrders('orderStatus', value)
                            }}
                        />
                    </Row>
                </div>
            }
        >
            <Space direction={'vertical'} style={{ width: '100%' }}></Space>
            <Row>
                <Col span={28}>
                    {contextHolder}
                    <Table
                        dataSource={orderData as any}
                        columns={columns}
                        size='large'
                        loading={{
                            spinning: loading || false,
                            indicator: <Spin indicator={<CustomLoader />} />,
                        }}
                        pagination={false}
                        rowKey="id"
                    />
                </Col>
            </Row>
        </Container >
    )
}

export default SubscriptionOrders